import {Loader} from '@googlemaps/js-api-loader';
import {MarkerClusterer} from '@googlemaps/markerclusterer';


export default class MerchantSearch {
    /**
     * MapConfig
     *
     * @typedef MapConfig
     * @type {Object}
     * @property {string} apiKey - The Google Maps API key.
     * @property {string} regionCodeIso2 - The region code.
     * @property {{lat: string, long: string}} center - The center coordinates.
     * @property {string} zoom - The initial zoom level.
     */

    /**
     * @type {MapConfig}
     */
    mapConfig = {};

    constructor(target, options = {}) {
        this.target = target;
        this.options = options;

        const config = target.getAttribute('data-merchant-search-config');
        if (!config) {
            console.error('Configuration element not found');
            return;
        }

        this.mapConfig = JSON.parse(config);

        if (!this.mapConfig.apiKey) {
            console.error('API key is missing');
            return;
        }

        this.mapConfig.regionCodeIso2 = this.mapConfig.regionCodeIso2 || 'de';
        this.mapId = target.getAttribute('data-ean');
        this.firstSearchTriggered = false;
        this.isMapSticky = false;
        this.markers = [];
        this.activeMarkerID = null;
        this.geoPosition = null;
        this.userPosition = null;
        this.icon = document.createElement('img');
        this.icon.src = this.target.getAttribute('data-icon-location') || '';
        this.iconActive = document.createElement('img');
        this.iconActive.src = this.target.getAttribute('data-icon-location-active') || '';
        this.clusterIcon = this.target.getAttribute('data-icon-cluster') || '';
        this.clusterIconActive = this.target.getAttribute('data-icon-cluster-active') || '';
        /** @type {HTMLButtonElement|null} */
        this.filterButtonOpen = this.target.querySelector('.overlay-filter-open');
        /** @type {HTMLElement|null} */
        this.filterOverlay = this.target.querySelector('.overlay-filter');
        this.isFilterOverlayActive = false;
        /** @type {HTMLFormElement|null} */
        this.searchForm = this.target.querySelector('form');
        this.searchFormInitialHeight = this.searchForm.offsetHeight;
        /** @type {HTMLInputElement} */
        this.searchField = this.target.querySelector('#merchant-search-term');
        /** @type {HTMLButtonElement|null} */
        this.buttonClear = this.target.querySelector('.clear-field');
        /** @type {HTMLDivElement} */
        this.resultList = this.target.querySelector('.merchant-search-result-list-wrapper');

        this.loadGoogleMapsAPI()
            .then(() => {
                this.init();
            });
    }

    async loadGoogleMapsAPI() {
        const loader = new Loader({
            apiKey: this.mapConfig.apiKey,
            version: 'weekly',
            libraries: ['places', 'geometry', 'marker'],
        });

        return await loader.importLibrary('maps').catch(error => {
            console.error('Error loading Google Maps:', error);
        });
    }

    init() {
        this.initMap().then(() => {
            this.bindHandler();
            this.geolocate(true);
            this.handleStickyMap();
        });
    }

    bindHandler() {
        this.searchForm?.addEventListener('submit', (e) => {
            e.preventDefault();

            this.firstSearchTriggered = true;

            const geocoder = new google.maps.Geocoder();
            geocoder.geocode(
                {
                    address: this.searchField.value,
                    region: this.mapConfig.regionCodeIso2
                }).then(geocoderResponse => {
                if (!geocoderResponse.results[0]?.geometry) return;

                const results = geocoderResponse.results[0];

                if (this.geoPosition == null) {
                    const position = results.geometry.location;
                    this.addPositionMarker(position);
                }

                let country = false,
                    locality = false,
                    continent = false;

                results.address_components.forEach((address) => {
                    if (country === false) {
                        country = address?.types.includes('country');
                    }

                    if (locality === false) {
                        locality = address?.types.includes('locality');
                    }

                    if (continent === false) {
                        continent = address?.types.includes('continent');
                    }
                });

                const lat = results.geometry.location.lat();
                const lng = results.geometry.location.lng();
                let visibleMarkers = 3;

                if (continent === true) {
                    visibleMarkers = 20;
                }

                if (country === true) {
                    visibleMarkers = 15;
                }

                if (locality === true) {
                    visibleMarkers = 5;
                }

                this.loadMerchants(
                    lat,
                    lng,
                    true,
                    true,
                    visibleMarkers
                );
            }).catch(() => {
                this.clearListItems();
                this.clearMarkers();
            });
        });

        this.filterButtonOpen?.addEventListener('click', () => {
            if (this.isFilterOverlayActive) {
                this.isFilterOverlayActive = false;
                this.deactivateFilterOverlay();
            } else {
                this.isFilterOverlayActive = true;
                this.activateFilterOverlay();
            }
        });

        this.target.querySelectorAll('.overlay-filter input[type="checkbox"]').forEach(checkbox => {
            checkbox.addEventListener('change', (e) => this.handleFilterChange(e));
        });

        this.target.addEventListener('click', (e) => {
            if (e.target.closest('.merchant-search-result-overlay .close')) {
                this.closeOverlay();
            }
        });

        this.resultList?.addEventListener('click', (e) => {
            const storeID = e.currentTarget.getAttribute('data-store-id');
            this.markers.forEach(marker => {
                if (marker.id === storeID) {
                    this.map.panTo({
                        lat: marker.position.lat(),
                        lng: marker.position.lng()
                    });

                    if (this.map.getZoom() !== 14) {
                        this.map.setZoom(14);
                    }

                    const markerIconActiveSrc = this.target.getAttribute('data-icon-location-active') || '';
                    this.setMarkerIcon(marker, markerIconActiveSrc);
                }
            });
        });

        this.resultList?.addEventListener(
            'mouseover',
            (e) => {
                const store = e.target.closest('li.merchant-search-store');

                if (store && this.resultList.contains(store)) {
                    const storeID = store.getAttribute('data-store-id');
                    const clusterIconActiveSrc = this.target.getAttribute('data-icon-cluster-active') || '';
                    const markerIconActiveSrc = this.target.getAttribute('data-icon-location-active') || '';

                    // Handle cluster icons
                    const clusters = this.markerClusterer.clusters;
                    clusters.forEach(cluster => {
                        if (cluster.markers.some(marker => marker.id === storeID)) {
                            if (cluster.marker?.content instanceof HTMLElement) {
                                cluster.marker.content.querySelector('img').src = clusterIconActiveSrc;
                            }
                        }
                    });

                    // Handle individual markers
                    this.markers.forEach(marker => {
                        if (marker.id === storeID) {
                            marker.content.style.transform = 'scale(1.2)';
                            marker.content.querySelector('img').src = markerIconActiveSrc;
                        }
                    });
                }
            },
            {
                passive: true
            }
        );

        this.resultList.addEventListener(
            'mouseout',
            (e) => {
                const store = e.target.closest('li.merchant-search-store');
                if (store && this.resultList.contains(store)) {
                    const clusterIconSrc = this.target.getAttribute('data-icon-cluster') || '';
                    const markerIconSrc = this.target.getAttribute('data-icon-location') || '';
                    const markerIconActiveSrc = this.target.getAttribute('data-icon-location-active') || '';

                    this.markerClusterer.clusters.forEach(cluster => {
                        if (cluster.marker) {
                            cluster.marker.content.querySelector('img').src = clusterIconSrc;
                        }
                    });

                    this.markers.forEach(marker => {
                        if (marker.id !== this.activeMarkerID) {
                            marker.content.style.transform = 'scale(1.0)';
                            marker.content.querySelector('img').src = markerIconSrc;
                        } else if (marker.id === this.activeMarkerID) {
                            marker.content.querySelector('img').src = markerIconActiveSrc;
                        }
                    });
                }
            },
            {
                passive: true
            }
        );

        window.addEventListener('scroll', () => this.handleStickyMap(), {passive: true});

        window.addEventListener('resize', () => {
            clearTimeout(this.timeoutFilter);
            this.timeoutFilter = setTimeout(() => {
                this.handleFilterListHeight();
            }, 300);
        }, {passive: true});

        this.searchField.addEventListener('input', (e) => {
            /** @type {HTMLInputElement} */
            const field = e.currentTarget;
            const value = field.value.trim();
            if (value !== '') {
                field.closest('.form-field-search').classList.add('has-value');
            } else {
                field.closest('.form-field-search').classList.remove('has-value');
            }
        });

        this.buttonClear.addEventListener('click', () => {
            this.searchField.value = '';
            this.searchField.closest('.form-field-search').classList.remove('has-value');
            this.searchField.focus();
        });

        this.debounceTimeout = null;
        this.map.addListener('dragend', () => {
            clearTimeout(this.debounceTimeout);
            this.debounceTimeout = setTimeout(() => {
                this.loadMerchantsOnBoundsChange();
            }, 500);
        });

        // Custom flag to track zoom changes
        this.map.getZoomChangedFlag = false;
        this.map.addListener('zoom_changed', () => {
            this.map.getZoomChangedFlag = true;
            setTimeout(() => {
                this.map.getZoomChangedFlag = false;
            }, 300);
        });
    }

    handleFilterChange() {
        const searchTerm = this.searchField.value.trim();
        let position;
        if (searchTerm !== '') {
            const geocoder = new google.maps.Geocoder();
            geocoder.geocode(
                {address: searchTerm},
                (results, status) => {
                    if (status === google.maps.GeocoderStatus.OK) {
                        if (!results[0].geometry) return;
                        this.loadMerchants(results[0].geometry.location.lat(), results[0].geometry.location.lng());
                    } else if (status === google.maps.GeocoderStatus.ZERO_RESULTS) {
                        this.loadMerchants(null, null);
                    } else {
                        console.warn('Geocoding Request failed due to status:', status);
                    }
                }
            );
        } else if (this.geoPosition || this.firstSearchTriggered) {
            position = new google.maps.LatLng(this.map.getCenter().lat(), this.map.getCenter().lng());
            this.loadMerchants(position.lat(), position.lng());
        }
    }

    async initMap() {
        const mapContainer = this.target.querySelector('#map');
        if (!mapContainer) {
            console.error('Map container element not found');
            return;
        }

        if (this.map) return;

        const center = {lat: parseFloat(this.mapConfig.center.lat), lng: parseFloat(this.mapConfig.center.long)};
        const zoom = parseInt(this.mapConfig.zoom) || 6;

        this.map = new google.maps.Map(mapContainer, {
            center: center,
            zoom: zoom,
            fullscreenControl: false,
            streetViewControl: false,
            mapTypeControl: false,
            zoomControlOptions: {
                position: google.maps.ControlPosition.RIGHT_TOP
            },
            mapId: this.mapId,
            gestureHandling: 'greedy', // Ensures the map only reacts to gesture input (e.g., touch, scroll)
            restriction: {
                latLngBounds: {
                    north: 85,
                    south: -85,
                    west: -180,
                    east: 180
                },
                strictBounds: true
            }
        });

        const clusterIconSrc = this.target.getAttribute('data-icon-cluster') || '';
        const clusterIconActiveSrc = this.target.getAttribute('data-icon-cluster-active') || '';

        const renderer = {
            render: ({count, position}) => {
                const content = document.createElement('div');
                content.style.position = 'relative';
                content.style.width = '53px';
                content.style.height = '52px';

                const img = document.createElement('img');
                img.src = clusterIconSrc;
                img.style.width = '100%';
                img.style.height = '100%';

                const label = document.createElement('div');
                label.style.position = 'absolute';
                label.style.top = '50%';
                label.style.left = '50%';
                label.style.transform = 'translate(-50%, -50%)';
                label.style.color = 'white';
                label.style.fontSize = '15px';
                label.style.fontWeight = 'bold';
                label.style.textAlign = 'center';
                label.style.width = '100%';
                label.style.height = '100%';
                label.style.display = 'flex';
                label.style.alignItems = 'center';
                label.style.justifyContent = 'center';
                label.textContent = String(count);

                content.appendChild(img);
                content.appendChild(label);

                const marker = new google.maps.marker.AdvancedMarkerElement({
                    content: content,
                    position: position,
                    map: this.map,
                    zIndex: 99999 + count
                });

                content.addEventListener('mouseover', () => {
                    img.src = clusterIconActiveSrc;
                }, {passive: true});

                content.addEventListener('mouseout', () => {
                    img.src = clusterIconSrc;
                }, {passive: true});

                return marker;
            }
        };

        this.markerClusterer = new MarkerClusterer({
            map: this.map,
            markers: this.markers,
            renderer: renderer,
        });

        if (navigator.geolocation) {
            this.createGeolocationControl();
        }

        await this.initAutocomplete();

        this.debounceTimeout = null;

        this.map.addListener('dragend', () => {
            clearTimeout(this.debounceTimeout);
            this.debounceTimeout = setTimeout(() => {
                this.loadMerchantsOnBoundsChange();
            }, 500);
        });

        this.map.addListener('zoom_changed', () => {
            this.map.getZoomChangedFlag = true;
            setTimeout(() => {
                this.map.getZoomChangedFlag = false;
            }, 300);
        });

        this.geolocate(true);
    }

    loadMerchantsOnBoundsChange() {
        if (!this.map.getZoomChangedFlag) {
            const center = this.map.getCenter();
            this.loadMerchants(center.lat(), center.lng(), false, false, null);
        }
    }

    async initAutocomplete() {
        const {Autocomplete} = await google.maps.importLibrary('places');

        if (!this.searchField) {
            console.error('Search field element not found');
            return;
        }

        const autocomplete = new Autocomplete(this.searchField, {
            fields: ['address_components', 'geometry', 'name'],
            strictBounds: false,
        });

        autocomplete.setComponentRestrictions({country: [this.mapConfig.regionCodeIso2]});

        autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace();
            if (!place.geometry) return;

            this.handlePlaceChanged(place);
        });

        this.map.addListener('center_changed', () => {
            autocomplete.setBounds(this.map.getBounds());
        });
    }

    handlePlaceChanged(place) {
        const geometry = place.geometry;
        const addressComponents = place.address_components;

        if (!geometry) return;

        const position = geometry.location;

        if (this.geoPosition == null) {
            this.addPositionMarker(position);
        }

        let country = false,
            locality = false,
            continent = false;

        addressComponents.forEach(component => {
            if (!country) country = component.types.includes('country');
            if (!locality) locality = component.types.includes('locality');
            if (!continent) continent = component.types.includes('continent');
        });

        const lat = position.lat(),
            lng = position.lng();
        let visibleMarkers = 3;

        if (continent) visibleMarkers = 20;
        if (country) visibleMarkers = 15;
        if (locality) visibleMarkers = 5;

        this.loadMerchants(lat, lng, true, true, visibleMarkers);
    }

    /**
     * @param {google.maps.LatLng} position
     */
    addPositionMarker(position) {
        if (!position || isNaN(position.lat()) || isNaN(position.lng())) {
            console.error('Invalid position:', position);
            return;
        }

        const initialZoom = this.map.getZoom();

        const radius = 2600000;
        if (this.userPositionRange == null) {
            const circle = new google.maps.Circle({
                center: position,
                radius: radius,
                strokeWeight: 0.5,
                strokeColor: '#02c0bf',
                strokeOpacity: 1,
                fillColor: '#00FFFE',
                fillOpacity: 0.2,
                visible: false
            });
            this.userPositionRange = circle;
            this.userPositionRange.setMap(this.map);

            this.map.addListener('zoom_changed', () => {
                const zoom = this.map?.getZoom();
                this.userPositionRange.setRadius((radius / Math.pow(2, zoom)) * Math.max(1, zoom - 7));
            });
        } else {
            this.userPositionRange?.setCenter(position);
        }
        this.userPositionRange?.setRadius((radius / Math.pow(2, initialZoom)) * Math.max(1, initialZoom - 7));

        // Create a div element for the custom marker
        const customMarkerDiv = document.createElement('div');
        customMarkerDiv.style.position = 'absolute';
        customMarkerDiv.style.width = '48px';
        customMarkerDiv.style.height = '48px';
        customMarkerDiv.style.transform = 'translate(-50%, -50%)';

        const outerCircle = document.createElement('div');
        outerCircle.style.position = 'absolute';
        outerCircle.style.width = '12px';
        outerCircle.style.height = '12px';
        outerCircle.style.backgroundColor = '#ffffff';
        outerCircle.style.borderRadius = '50%';
        outerCircle.style.top = '50%';
        outerCircle.style.left = '50%';
        outerCircle.style.transform = 'translate(-50%, -50%)';
        outerCircle.style.pointerEvents = 'none';

        const innerCircle = document.createElement('div');
        innerCircle.style.position = 'absolute';
        innerCircle.style.width = '9px';
        innerCircle.style.height = '9px';
        innerCircle.style.backgroundColor = '#007fff';
        innerCircle.style.borderRadius = '50%';
        innerCircle.style.top = '50%';
        innerCircle.style.left = '50%';
        innerCircle.style.transform = 'translate(-50%, -50%)';
        innerCircle.style.pointerEvents = 'none';

        // Append the circle and icon to the custom marker div
        customMarkerDiv.appendChild(outerCircle);
        customMarkerDiv.appendChild(innerCircle);

        // If the user position marker already exists, just update its position and content
        if (this.userPosition) {
            this.userPosition.position = position;
            this.userPosition.content = customMarkerDiv;
        } else {
            // Create a new AdvancedMarkerElement for the user's position
            this.userPosition = new google.maps.marker.AdvancedMarkerElement({
                map: this.map,
                position,
                title: 'You are here',
                content: customMarkerDiv,
                zIndex: google.maps.Marker.MAX_ZINDEX
            });
        }

        setTimeout(() => {
            this.userPositionRange?.setVisible(true);
        }, 200);
    }

    /**
     * @param {Number|null} lat
     * @param {Number|null} lng
     * @param {boolean} shouldMapFitBounds
     * @param {boolean} boundsFitToVisibleMarkers
     * @param {Number} numOfVisibleMarkers
     */
    loadMerchants(lat, lng, shouldMapFitBounds = false, boundsFitToVisibleMarkers = false, numOfVisibleMarkers = 2) {
        if (lat === null || lng === null) {
            console.warn('Lat or Lng is null');
            return;
        }

        const url = new URL(this.target.querySelector('form')?.getAttribute('action'), window.location.origin)?.toString();
        const position = new google.maps.LatLng(lat, lng);

        const formData = new FormData(this.searchForm);
        formData.append('tx_avmplugins_merchantsearch[latitude]', position.lat().toString());
        formData.append('tx_avmplugins_merchantsearch[longitude]', position.lng().toString());

        const patternsToRemove = [
            'tx_avmplugins_merchantsearch[__referrer]',
            'tx_avmplugins_merchantsearch[__trustedProperties]'
        ];
        const data = this.filterFormData(formData, patternsToRemove);

        this.showLoader();

        fetch(url, {
            method: 'POST',
            body: data,
        })
            .then(response => response.json())
            .then(response => {
                this.clearMarkers();
                this.clearListItems();
                if (response.markup && response.markup.includes('<div class="merchant-search-list-inner">')) {
                    const list = document.createElement('div');
                    list.innerHTML = response.markup;
                    const ulElement = list.querySelector('ul');
                    const stores = list.querySelectorAll('.merchant-search-store');
                    const markers = response.coordinates;
                    stores.forEach((store, index) => {
                        let distanceMeters = '';
                        let distanceKilometers = '';
                        let location = null;
                        const storeLat = parseFloat(store.getAttribute('data-store-lat'));
                        const storeLng = parseFloat(store.getAttribute('data-store-lng'));
                        const storeLocation = new google.maps.LatLng(storeLat, storeLng);
                        let shouldCalculateDistance = false;

                        if (this.geoPosition) {
                            shouldCalculateDistance = true;
                            location = new google.maps.LatLng(this.geoPosition.coords.latitude, this.geoPosition.coords.longitude);
                        } else if (this.userPosition) {
                            shouldCalculateDistance = true;
                            location = new google.maps.LatLng(this.userPosition?.position?.lat, this.userPosition?.position?.lng);
                        }

                        if (shouldCalculateDistance) {
                            distanceMeters = this.getDistanceFromLatLng(
                                {lat: location?.lat(), lng: location?.lng()},
                                {lat: storeLocation?.lat(), lng: storeLocation?.lng()}
                            );

                            distanceMeters = Math.round(distanceMeters);
                            distanceKilometers = (distanceMeters / 1000).toFixed(2).replace('.', ',');

                            store.querySelector('.merchant-search-store-distance').innerHTML = `${distanceKilometers} km`;
                        }

                        if (index > 19) {
                            store.classList.add('invisible');
                        }

                        if (ulElement) {
                            ulElement.appendChild(store);
                        }
                    });

                    if (markers && markers.length > 0) {

                        markers.forEach(markerData => {
                            this.addMarker({
                                id: markerData.id,
                                latitude: markerData.lat,
                                longitude: markerData.lng,
                                name: markerData.name,
                            });
                        });

                        if (shouldMapFitBounds) {
                            const bounds = new google.maps.LatLngBounds();
                            const relevantMarkers = boundsFitToVisibleMarkers ? markers.slice(0, numOfVisibleMarkers - 1) : markers;
                            relevantMarkers.forEach(markerData => {
                                const markerPosition = new google.maps.LatLng(markerData.lat, markerData.lng);
                                bounds.extend(markerPosition);
                            });
                            this.fitMapToBounds(bounds); // Use the fitMapToBounds method to zoom to the bounds of the markers
                        } else {
                            this.map.setCenter(position);
                        }
                    } else {
                        console.warn('No coordinates found in response.');
                    }

                    this.resultList.appendChild(list);
                    this.handleInfoIcon();
                    this.handleFilterListHeight();
                } else {
                    console.warn('No merchant list entries available or incorrect markup.');
                }
            })
            .catch(console.error)
            .finally(() => this.hideLoader());
    }


    async addMarker({id, latitude, longitude, name}) {
        const {AdvancedMarkerElement} = await google.maps.importLibrary('marker');

        if (isNaN(latitude) || isNaN(longitude)) {
            console.error('Invalid latitude or longitude:', latitude, longitude);
            return;
        }

        const position = new google.maps.LatLng(parseFloat(latitude), parseFloat(longitude));

        const markerIconSrc = this.target.getAttribute('data-icon-location') || '';
        const markerIconActiveSrc = this.target.getAttribute('data-icon-location-active') || '';

        // Create the custom content element
        const content = document.createElement('div');
        content.style.position = 'relative';
        content.style.width = '40px';
        content.style.height = '40px';

        const img = document.createElement('img');
        img.src = markerIconSrc;
        img.style.width = '100%';
        img.style.height = '100%';

        content.appendChild(img);

        const marker = new AdvancedMarkerElement({
            map: this.map,
            position,
            title: name,
            content: content,
            zIndex: 1,
        });
        marker.id = id;

        // Event listeners for mouseover and mouseout on the content
        content.addEventListener('mouseover', () => {
            if (marker.id !== this.activeMarkerID) {
                img.src = markerIconActiveSrc;
                content.style.transform = 'scale(1.2)';
                this.highlightStore(id, true);
            }
        }, {passive: true});

        content.addEventListener('mouseout', () => {
            if (marker.id !== this.activeMarkerID) {
                content.style.transform = 'scale(1.0)';
                img.src = markerIconSrc;
                this.highlightStore(id, false);
            }
        }, {passive: true});

        marker.addListener('click', () => this.markerClickHandler(marker));

        this.markers.push(marker);

        if (this.markerClusterer) {
            this.markerClusterer.addMarker(marker);
        } else {
            console.warn('MarkerClusterer is not initialized yet');
        }
    }

    highlightStore(storeId, highlight) {
        const storeElement = document.querySelector(`li[data-store-id="${storeId}"]`);
        if (storeElement) {
            if (highlight) {
                storeElement.classList.add('merchant-search-store-highlight');
            } else {
                storeElement.classList.remove('merchant-search-store-highlight');
            }
        }
    }

    getDistanceFromLatLng(point1, point2) {
        const R = 6371e3; // metres
        const lat1 = point1.lat * Math.PI / 180;
        const lat2 = point2.lat * Math.PI / 180;
        const deltaLat = (point2.lat - point1.lat) * Math.PI / 180;
        const deltaLng = (point2.lng - point1.lng) * Math.PI / 180;

        const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
            Math.cos(lat1) * Math.cos(lat2) *
            Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

        // return distance in metres
        return R * c;
    }

    async markerClickHandler(marker) {
        this.loadOverlay(marker.id);

        // Reset the icons for all markers
        this.markers.forEach(m => {
            if (m instanceof google.maps.marker.AdvancedMarkerElement) {
                const markerIconSrc = this.target.getAttribute('data-icon-location') || '';
                this.setMarkerIcon(m, markerIconSrc);
            }
        });

        // Set the active icon for the clicked marker
        if (marker instanceof google.maps.marker.AdvancedMarkerElement) {
            const markerIconActiveSrc = this.target.getAttribute('data-icon-location-active') || '';
            this.setMarkerIcon(marker, markerIconActiveSrc);
        }

        // Update the active marker ID
        this.activeMarkerID = marker.id;
    }

    clearMarkers() {
        this.markers.forEach(marker => marker.map = null);
        this.markers = [];
        if (this.markerClusterer) {
            this.markerClusterer.clearMarkers();
        }
    }

    clearListItems() {
        this.resultList.innerHTML = '';
    }

    fitMapToBounds(bounds) {
        this.map.fitBounds(bounds);
    }

    handleStickyMap() {
        const mapWrapperOuter = this.target.querySelector('.map-wrapper-outer');
        const mapOffset = mapWrapperOuter.getBoundingClientRect().top + window.scrollY;
        const scrollOffset = window.scrollY;

        if (scrollOffset < mapOffset) {
            this.target.classList.remove('map-sticky');
            this.isMapSticky = false;
            return;
        }

        if (window.matchMedia('(max-width: 985px)').matches) {
            if (!this.isMapSticky) {
                this.target.classList.add('map-sticky');
                this.isMapSticky = true;
            }
        } else {
            this.target.classList.remove('map-sticky');
            this.isMapSticky = false;
        }
    }

    handleInfoIcon() {
        this.resultList.querySelectorAll('.merchant-search-list .merchant-search-store .store-info').forEach(infoIcon => {
            infoIcon.addEventListener('click', (e) => {
                /** @type {HTMLElement} */
                const infoIcon = e.currentTarget;
                const store = infoIcon.closest('[data-store-id]');
                const id = store.getAttribute('data-store-id');
                this.markers.forEach(marker => {
                    if (marker.id === id) {
                        const markerIconActiveSrc = this.target.getAttribute('data-icon-location-active') || '';
                        this.activeMarkerID = id;
                        this.setMarkerIcon(marker, markerIconActiveSrc);
                    }
                });
                this.loadOverlay(id);
            });
        });
    }

    showLoader() {
        this.target.classList.add('loader-active');
    }

    hideLoader() {
        this.target.classList.remove('loader-active');
    }

    createGeolocationControl() {
        const controlDiv = document.createElement('div');
        const controlUI = document.createElement('div');
        controlUI.style.backgroundColor = '#fff';
        controlUI.style.backgroundPosition = 'left top';
        controlUI.style.height = '40px';
        controlUI.style.width = '40px';
        controlUI.style.cursor = 'pointer';
        controlUI.style.marginRight = '10px';
        controlUI.style.marginTop = '10px';
        controlUI.style.borderRadius = '2px';
        controlUI.style.boxShadow = 'rgba(0, 0, 0, 0.3) 0px 1px 4px -1px';
        controlUI.title = 'Click to center map on your location';

        const image = document.createElement('div');
        image.style.backgroundImage = 'url(' + this.target.getAttribute('data-icon-gps') + ')';
        image.style.backgroundRepeat = 'no-repeat';
        image.style.backgroundPosition = 'center center';
        image.style.backgroundSize = '24px auto';
        image.style.height = '40px';
        image.style.width = '40px';

        controlUI.appendChild(image);
        controlDiv.appendChild(controlUI);
        this.map.controls[google.maps.ControlPosition.RIGHT_TOP].push(controlDiv);

        controlUI.addEventListener('click', () => this.geolocate(true));
    }

    geolocate(showMessages) {
        if (this.geolocateInProgress) {
            return;
        }

        this.geolocateInProgress = true;

        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    this.geoPosition = position;
                    const pos = new google.maps.LatLng(position.coords.latitude, position.coords.longitude);
                    this.addPositionMarker(pos);
                    this.loadMerchants(
                        pos.lat(),
                        pos.lng(),
                        true,
                        true,
                        5
                    );
                    this.geolocateInProgress = false;
                },
                () => {
                    this.geoPosition = null;
                    this.clearMarkers();
                    this.searchField.value = '';
                    if (showMessages) {
                        this.showMessage('Geolocation error.');
                    }
                    this.geolocateInProgress = false;
                },
                {enableHighAccuracy: true, maximumAge: 50000}
            );
        } else {
            if (showMessages) {
                this.showMessage('Geolocation not supported.');
            }
            this.geolocateInProgress = false;
        }
    }

    showMessage(message) {
        const messageContainer = this.target.querySelector('.merchant-search-message-container');
        const messageDiv = document.createElement('div');
        messageDiv.className = 'merchant-search-message';
        messageDiv.textContent = message;
        messageContainer.appendChild(messageDiv);
        setTimeout(() => messageDiv.remove(), 4000);
    }

    handleFilterListHeight() {
        const searchFormContainer = this.target.querySelector('.merchant-search-form');
        if (this.isFilterOverlayActive === false) {
            searchFormContainer.style.removeProperty('height');
            this.resultList.style.removeProperty('height');
            this.resultList.style.removeProperty('min-height');
        } else {
            setTimeout(() => {
                const formHeight = this.searchForm.offsetHeight;
                const listHeight = this.target.querySelector('.merchant-search-column.column-left').offsetHeight;
                searchFormContainer.style.height = 'auto';
                Object.assign(this.resultList.style, {
                    height: `${listHeight - formHeight - 2}px`,
                    minHeight: '0'
                });
            }, 1);
        }
    }

    loadOverlay(storeId) {
        const url = this.searchForm.getAttribute('action');
        const data = {
            'tx_avmplugins_merchantsearch[id]': storeId,
            'tx_avmplugins_merchantsearch[action]': 'store',
        };
        this.showLoader();

        fetch(url, {
            method: 'POST',
            headers: {'Content-Type': 'application/x-www-form-urlencoded'},
            body: new URLSearchParams(data).toString(),
        })
            .then(response => response.json())
            .then(response => {
                const hasMarkup = response.markup && response.markup.trim() !== '';
                if (hasMarkup) {
                    const overlay = this.target.querySelector('.merchant-search-result-overlay .merchant-search-overlay');
                    overlay && overlay.remove();
                    const overlayContainer = this.target.querySelector('.merchant-search-result-overlay');
                    overlayContainer.insertAdjacentHTML('beforeend', response.markup);
                    this.overlay = overlayContainer.querySelector('.merchant-search-result-overlay .merchant-search-overlay');

                    const destination = new google.maps.LatLng(response.latitude, response.longitude);
                    if (this.geoPosition || this.userPosition) {
                        this.calculateOverlayDurations(destination);
                    } else {
                        this.overlay.classList.add('overlay-active');
                        this.hideLoader();
                        console.warn('Could not calculate travel durations. Geolocation or destination not found.');
                    }

                    const routeLinkWrapper = this.overlay.querySelector('.merchant-search-route');
                    if (this.userPosition) {
                        const link = document.createElement('a');
                        link.href = `https://www.google.com/maps/dir/${this.userPosition?.position?.lat},${this.userPosition?.position?.lng}/${routeLinkWrapper.dataset.position}`;
                        link.textContent = routeLinkWrapper.dataset.label;
                        link.className = 'button';
                        link.target = '_blank';
                        routeLinkWrapper.appendChild(link);
                    } else {
                        const link = document.createElement('a');
                        link.href = `https://www.google.com/maps/dir//${routeLinkWrapper.dataset.position}`;
                        link.textContent = routeLinkWrapper.dataset.label;
                        link.className = 'button';
                        link.target = '_blank';
                        routeLinkWrapper.appendChild(link);
                    }

                    this.markers.forEach(marker => {
                        if (marker.id === response.id) {
                            const markerIconActiveSrc = this.target.getAttribute('data-icon-location-active') || '';
                            marker.style.transform = 'scale(1.2)';
                            this.setMarkerIcon(marker, markerIconActiveSrc);
                        }
                    });
                } else {
                    this.hideLoader();
                }
            })
            .catch(console.error)
            .finally(() => this.hideLoader());
    }

    calculateOverlayDurations(destination) {
        const userPosition = this.geoPosition
            ? new google.maps.LatLng(this.geoPosition.coords.latitude, this.geoPosition.coords.longitude)
            : new google.maps.LatLng(this.userPosition?.position?.lat, this.userPosition?.position?.lng);

        this.overlay.classList.add('overlay-active');

        const requests = [
            {travelMode: google.maps.TravelMode.DRIVING, element: '.driving'},
            {travelMode: google.maps.TravelMode.WALKING, element: '.walking'},
            {travelMode: google.maps.TravelMode.TRANSIT, element: '.transit'}
        ];

        requests.forEach(request => {
            const directionsService = new google.maps.DirectionsService();
            directionsService.route({origin: userPosition, destination, travelMode: request.travelMode}, (response, status) => {
                if (status === google.maps.DirectionsStatus.OK) {
                    const duration = response.routes[0].legs[0].duration.text;
                    this.overlay.querySelector(`.merchant-search-durations ${request.element}`).textContent = duration;
                    this.overlay.querySelector(`.merchant-search-durations ${request.element}`).classList.remove('invisible');
                } else {
                    this.overlay.querySelector(`.merchant-search-durations ${request.element}`).classList.add('invisible');
                    console.warn(`Directions request failed due to ${status}`);
                }
            });
        });

        this.hideLoader();
    }

    closeOverlay() {
        if (this.activeMarkerID) {
            const activeMarker = this.markers.find(marker => marker.id === this.activeMarkerID);
            if (activeMarker) {
                const markerIconSrc = this.target.getAttribute('data-icon-location') || '';
                activeMarker.style.transform = 'scale(1.0)';
                this.setMarkerIcon(activeMarker, markerIconSrc);
                this.activeMarkerID = null;
            }
        }

        if (!this.overlay) return;

        this.overlay.classList.remove('overlay-active');
        setTimeout(() => {
            this.overlay.remove();
            this.overlay = null;
        }, 300);
    }

    activateFilterOverlay() {
        this.filterOverlay.classList.add('js-active');
        this.handleFilterListHeight();
        this.target.classList.add('filter-overlay-active');
    }

    deactivateFilterOverlay() {
        this.filterOverlay.classList.remove('js-active');
        this.handleFilterListHeight();
        this.target.classList.remove('filter-overlay-active');
    }

    setMarkerIcon(marker, icon) {
        const content = marker.content;
        const img = content.querySelector('img');
        if (img) {
            img.src = icon;
        }
    }

    /**
     * Filters the FormData object to exclude specific patterns from the keys.
     *
     * @param originalFormData
     * @param patternsToExclude
     * @returns {FormData}
     */
    filterFormData(originalFormData, patternsToExclude) {
        const filteredFormData = new FormData();

        for (const [key, value] of originalFormData.entries()) {
            if (!patternsToExclude.some(pattern => key.includes(pattern))) {
                filteredFormData.append(key, value);
            }
        }

        return filteredFormData;
    }
}
